/* A sale or a purchase, step by step (PK-65): one screen for a quote, order, delivery, invoice,
 * purchase order, receipt, bill or payment.
 *
 * Owner, 2026-10-09: "ERP UI is a superadmin panel like and user cannot go and understand that";
 * "if invoice is submitted and the next step is 'payment entry' and only that option should be
 * shown or there can be exception on credit etc."; and the lifecycle -- "invoice submit, payment
 * entry, ... delivery note" -- tracked in one place.
 *
 * What it shows is the gateway's (erp/flow.py): where this document is in its chain, the one next
 * step, and only the real exceptions. A step the books are not ready for says what to set up
 * instead of failing inside a form. Nothing here knows a pack; Jobs, the chat and the ERP all open
 * their money here.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Failed, Loading, useToast } from '@jackdesk/shell'
import { newIdempotencyKey, type ErpError } from '../api'
import { useErp } from '../context'
import { formatCurrency, formatDate } from '../format'
import { PrintDialog } from '../PrintDialog'
import type { DocFlow as Flow, FlowStep, Payment, SendRequest } from './types'
import { amountOf, docWord, stageWord } from './words'

/** What a customer is sent: what they owe, what was agreed, what they paid. */
const SENDABLE = new Set(['Quotation', 'Sales Order', 'Delivery Note', 'Sales Invoice', 'Payment Entry'])

export function DocFlow({ doctype, name, onOpen, onEdit, onSend, sendCheck, channelsUrl, booksUrl }: {
  doctype: string
  name: string
  /** Open another document of the chain (or the one a step just made). */
  onOpen: (doctype: string, name: string) => void
  /** Change a draft's lines, prices or dates: the full form. */
  onEdit?: (doctype: string, name: string) => void
  /** Where the books are set up (the Store), for a step the books are not ready for. */
  booksUrl?: string
  /** Send it to the customer on their own channel (the desk: WhatsApp, email…); returns what was done. */
  onSend?: (doc: SendRequest) => Promise<string>
  /** Can it reach the customer on any channel? null: yes; else why not -- Send is disabled and says so
   *  (owner, 2026-10-09: "the buttons should be disabled ... and ask user to connect"). */
  sendCheck?: (doc: SendRequest) => Promise<string | null>
  /** Where channels are connected, linked from that reason. */
  channelsUrl?: string
}) {
  const erp = useErp()
  const toast = useToast()
  const [flow, setFlow] = useState<Flow | null>(null)
  const [failed, setFailed] = useState<ErpError | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [paying, setPaying] = useState<FlowStep | null>(null)
  const [printing, setPrinting] = useState(false)
  const [sending, setSending] = useState(false)
  // undefined: not asked yet; null: it can go; a sentence: why not.
  const [sendWhy, setSendWhy] = useState<string | null | undefined>(undefined)

  const load = useCallback((signal?: AbortSignal) => {
    setFailed(null)
    erp.docFlow(doctype, name, signal).then(setFlow).catch((e) => {
      if ((e as Error).name !== 'AbortError') setFailed(e as ErpError)
    })
  }, [erp, doctype, name])
  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  async function take(step: FlowStep, payment?: Payment) {
    setBusy(step.label)
    try {
      const done = await erp.takeStep(doctype, name, { action: step.action, target: step.target, payment },
                                      newIdempotencyKey())
      setPaying(null)
      if (done.made && done.made.name !== name && step.action !== 'pay') {
        toast('ok', `${docWord(done.made.doctype, step.action === 'return')} ${done.made.name} made as a draft: check it, then finalise it.`)
        onOpen(done.made.doctype, done.made.name)
      } else {
        toast('ok', step.action === 'pay' ? 'Payment recorded.' : `${step.label}: done.`)
        load()
      }
    } catch (e) {
      toast('error', (e as ErpError).message)
    } finally {
      setBusy(null)
    }
  }

  const request: SendRequest | null = flow ? {
    doctype: flow.doctype, name: flow.name, title: flow.title,
    total: Number(flow.totals.rounded_total || flow.totals.grand_total || flow.totals.paid_amount || 0) || null,
    currency: flow.currency, reach: flow.reach,
  } : null
  const askKey = flow && flow.docstatus === 1 && sendCheck ? `${flow.doctype}/${flow.name}` : ''
  useEffect(() => {
    if (!askKey || !request || !sendCheck) return
    let live = true
    sendCheck(request).then((why) => { if (live) setSendWhy(why) }).catch(() => { if (live) setSendWhy(null) })
    return () => { live = false }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askKey])

  if (failed) return <Failed message={failed.message} requestId={failed.requestId} onRetry={() => load()} />
  if (!flow) return <Loading />

  const word = docWord(flow.doctype, flow.is_return)
  const money = (v: unknown) => formatCurrency(v, flow.currency)
  const owed = Number(flow.totals.outstanding_amount ?? 0)
  const total = flow.totals.rounded_total || flow.totals.grand_total
  const blocked = flow.blockers.length > 0
  // Sent through the host's channels (the desk chooses WhatsApp or email, PK-69); none here alone.
  const sendable = flow.docstatus === 1 && Boolean(onSend) && SENDABLE.has(flow.doctype) && flow.flow === 'sales'
  const sendBlocked = typeof sendWhy === 'string'
  async function sendNow() {
    if (!onSend || !request || sendBlocked) return
    setSending(true)
    try {
      toast('ok', await onSend(request))
    } catch (e) {
      // Closing the host's chooser is not a failure.
      if ((e as Error).name !== 'AbortError') toast('error', (e as Error).message || 'It could not be sent.')
    } finally {
      setSending(false)
    }
  }
  return (
    <div className="page money-flow" aria-label={`${word} ${flow.name}`}>
      <header className="money-head">
        <div style={{ minWidth: 0 }}>
          <div className="small muted">{word} · {flow.name}{flow.date ? ` · ${formatDate(flow.date)}` : ''}</div>
          <h1 className="money-title">{flow.title ?? flow.party ?? flow.name}</h1>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <span className={`chip chip-${flow.docstatus === 2 ? 'danger' : flow.docstatus === 0 ? 'draft' : owed > 0.005 ? 'warn' : 'ok'}`}>
              {flow.docstatus === 0 ? 'Draft' : flow.status}
            </span>
            {flow.due && owed > 0.005 ? <span className="small muted">due {formatDate(flow.due)}</span> : null}
          </div>
        </div>
        <div className="money-figures">
          {total !== undefined ? <div><span className="small muted">Total</span><b>{money(total)}</b></div> : null}
          {flow.totals.outstanding_amount !== undefined && flow.docstatus === 1 && !flow.is_return ? (
            <div><span className="small muted">{flow.flow === 'sales' ? 'Still owed' : 'Still to pay'}</span>
              <b style={owed > 0.005 ? { color: 'var(--warn)' } : undefined}>{money(owed)}</b></div>
          ) : null}
          {flow.doctype === 'Payment Entry' && flow.totals.paid_amount !== undefined ? (
            <div><span className="small muted">Paid</span><b>{money(flow.totals.paid_amount)}</b></div>
          ) : null}
        </div>
      </header>

      {flow.stages.length ? (
        <ol className="money-stages" aria-label="Where it is">
          {flow.stages.map((s) => (
            <li key={s.key} className="money-stage" data-state={s.state} data-here={s.here ? '1' : undefined}
                data-next={s.next ? '1' : undefined}>
              <div className="money-stage-h">
                <span className="money-dot" aria-hidden="true" />
                <b>{s.label}</b>
                <span className="small muted">{s.here ? 'Here' : s.next ? 'Next' : stageWord(s.state)}</span>
              </div>
              {s.docs.map((d) => (
                <button key={d.name} className="money-doc" disabled={d.name === flow.name}
                        onClick={() => onOpen(s.doctype, d.name)}>
                  <span>{d.is_return ? docWord(s.doctype, true) : d.name}</span>
                  {amountOf(d) !== null ? <span className="muted">{money(amountOf(d))}</span> : null}
                  {d.docstatus === 0 ? <span className="chip chip-draft">Draft</span> : null}
                </button>
              ))}
            </li>
          ))}
        </ol>
      ) : null}

      <section className="card card-pad money-next" aria-label="Next step">
        {flow.next ? (
          <>
            <div style={{ minWidth: 0 }}>
              <div className="small muted">Next step</div>
              <b>{flow.next.label}</b>
              <div className="small muted">{flow.next.why}</div>
              {blocked ? (
                <div className="money-blocked" role="alert">
                  Before this, your books need:
                  <ul>{flow.blockers.map((b) => <li key={b.key}><b>{b.title}</b> — {b.why}</li>)}</ul>
                  {booksUrl ? <a href={booksUrl} target="_blank" rel="noreferrer">Set them up in the Store → Settings → Your books</a>
                    : 'Set them up in the Store → Settings → Your books.'}
                </div>
              ) : null}
            </div>
            <button className="btn btn-primary" disabled={busy !== null || blocked}
                    onClick={() => (flow.next!.action === 'pay' ? setPaying(flow.next) : void take(flow.next!))}>
              {busy === flow.next.label ? 'Working…' : flow.next.label}
            </button>
          </>
        ) : (
          <div><div className="small muted">Next step</div><b>{flow.done ?? 'Nothing to do.'}</b></div>
        )}
        {flow.also.length ? (
          <div className="money-also">
            <span className="small muted">Or, when it applies:</span>
            {flow.also.map((a) => (
              <button key={`${a.action}-${a.target}`} className="btn btn-sm" disabled={busy !== null} title={a.why}
                      onClick={() => void take(a)}>{busy === a.label ? 'Working…' : a.label}</button>
            ))}
          </div>
        ) : null}
        {sendable ? (
          <button className="btn btn-sm money-send" disabled={sending || sendBlocked || sendWhy === undefined && Boolean(sendCheck)}
                  title={sendWhy ?? undefined} aria-describedby={sendBlocked ? 'money-send-why' : undefined}
                  onClick={() => void sendNow()}>
            {sending ? 'Sending…' : 'Send to the customer'}</button>
        ) : null}
        {flow.docstatus === 1 ? (
          <button className="btn btn-sm btn-quiet money-print" onClick={() => setPrinting(true)}>Print or send</button>
        ) : flow.docstatus === 0 && onEdit ? (
          <button className="btn btn-sm btn-quiet money-print" onClick={() => onEdit(flow.doctype, flow.name)}>
            Change the draft
          </button>
        ) : null}
        {sendable && sendBlocked ? (
          <p id="money-send-why" className="small muted money-send-why">
            Not sendable yet: {sendWhy}
            {channelsUrl ? <> <a href={channelsUrl} target="_blank" rel="noreferrer">Connect a channel</a></> : null}
          </p>
        ) : null}
      </section>

      {flow.items.length ? (
        <section className="card" aria-label="What it is for" style={{ overflowX: 'auto' }}>
          <table className="tbl">
            <thead><tr><th>Item</th><th className="num">Qty</th><th className="num">Rate</th><th className="num">Amount</th></tr></thead>
            <tbody>
              {flow.items.map((i, n) => (
                <tr key={n} style={{ cursor: 'default' }}>
                  <td><b>{i.item_name}</b>{i.item_name !== i.item_code ? <div className="small muted">{i.item_code}</div> : null}</td>
                  <td className="num">{i.qty} {i.uom}</td>
                  <td className="num">{money(i.rate)}</td>
                  <td className="num">{money(i.amount)}</td>
                </tr>
              ))}
              {flow.taxes.map((t, n) => (
                <tr key={`t${n}`} style={{ cursor: 'default' }}>
                  <td colSpan={3} className="small">{t.description}</td><td className="num">{money(t.amount)}</td>
                </tr>
              ))}
              {total !== undefined ? (
                <tr style={{ cursor: 'default' }}><td colSpan={3}><b>Total</b></td><td className="num"><b>{money(total)}</b></td></tr>
              ) : null}
            </tbody>
          </table>
        </section>
      ) : null}

      {flow.references.length ? (
        <section className="card card-pad small" aria-label="What it settles">
          <b>Settles</b>
          {flow.references.map((r) => (
            <button key={r.name} className="money-doc" onClick={() => onOpen(r.doctype, r.name)}>
              <span>{docWord(r.doctype)} {r.name}</span><span className="muted">{money(r.allocated)}</span>
            </button>
          ))}
        </section>
      ) : null}

      {paying ? <PayDialog flow={flow} step={paying} busy={busy !== null} onClose={() => setPaying(null)}
                           onPay={(p) => void take(paying, p)} /> : null}
      {printing ? (
        <PrintDialog doctype={flow.doctype} name={flow.name} onClose={() => setPrinting(false)}
                     onSend={sendable && !sendBlocked ? () => { setPrinting(false); void sendNow() } : undefined}
                     sendWhy={sendable && sendBlocked ? sendWhy : undefined} channelsUrl={channelsUrl} />
      ) : null}
    </div>
  )
}

/** Record a payment: how much (what is owed, or part of it), how, when, and the bank's reference
 *  when it went through a bank. */
function PayDialog({ flow, step, busy, onClose, onPay }: {
  flow: Flow; step: FlowStep; busy: boolean; onClose: () => void; onPay: (p: Payment) => void
}) {
  const erp = useErp()
  const owed = Number(flow.totals.outstanding_amount ?? 0)
  const [amount, setAmount] = useState(String(owed))
  const [modes, setModes] = useState<{ name: string; type: string }[]>([])
  const [mode, setMode] = useState('')
  const [reference, setReference] = useState('')
  const [when, setWhen] = useState(() => new Date().toISOString().slice(0, 10))
  useEffect(() => {
    const controller = new AbortController()
    erp.list('Mode of Payment', { fields: ['name', 'type'], filters: [['enabled', '=', 1]], limit: 50 }, controller.signal)
      .then((r) => setModes(r.rows as { name: string; type: string }[]))
      .catch(() => setModes([]))
    return () => controller.abort()
  }, [erp])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  const bank = useMemo(() => modes.find((m) => m.name === mode)?.type === 'Bank', [modes, mode])
  const value = Number(amount)
  const valid = value > 0 && value <= owed + 0.005 && mode && (!bank || reference.trim())

  return (
    <div className="palette-wrap" role="dialog" aria-modal="true" aria-label={step.label}
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <form className="money-dialog" onSubmit={(e) => {
        e.preventDefault()
        if (valid) onPay({ amount: value, mode_of_payment: mode, posting_date: when,
                           ...(reference.trim() ? { reference_no: reference.trim(), reference_date: when } : {}) })
      }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <b>{step.label}</b>
          <button type="button" className="btn btn-quiet btn-sm" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="small muted">{flow.title} · {docWord(flow.doctype)} {flow.name} · still owed {formatCurrency(owed, flow.currency)}</div>
        <label className="field-row"><span>Amount</span>
          <input className="input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} aria-label="Amount" />
        </label>
        {value > 0 && value < owed - 0.005 ? <div className="small muted">Part of it: {formatCurrency(owed - value, flow.currency)} will still be owed.</div> : null}
        {value > owed + 0.005 ? <div className="small" style={{ color: 'var(--danger)' }}>More than is owed.</div> : null}
        <label className="field-row"><span>How</span>
          <select className="select" value={mode} onChange={(e) => setMode(e.target.value)} aria-label="How it was paid">
            <option value="">Choose…</option>
            {modes.map((m) => <option key={m.name} value={m.name}>{m.name}</option>)}
          </select>
        </label>
        <label className="field-row"><span>On</span>
          <input className="input" type="date" value={when} onChange={(e) => setWhen(e.target.value)} aria-label="Paid on" />
        </label>
        {bank ? (
          <label className="field-row"><span>Bank reference</span>
            <input className="input" value={reference} onChange={(e) => setReference(e.target.value)}
                   placeholder="UTR or cheque number" aria-label="Bank reference" />
          </label>
        ) : null}
        <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!valid || busy}>{busy ? 'Recording…' : 'Record it'}</button>
        </div>
      </form>
    </div>
  )
}
